import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Alert,
  LinearProgress,
} from "@mui/material";
import {
  LocalShipping,
  Warning,
  CreditCard,
  CheckCircle,
  UploadFile,
} from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  fetchShipments,
  selectShipments,
  selectShipmentsLoading,
} from "../../redux/slices/shipmentSlice";
import {
  fetchNotifications,
  markAsRead,
  selectNotifications,
  selectUnreadCount,
} from "../../redux/slices/notificationSlice";
import { selectCurrentUser, selectActiveTin } from "../../redux/slices/authSlice";
import { selectMyUnclaimedPackagesCount } from "../../redux/slices/unclaimedPackagesSlice";
import { selectKeyForRole } from "../../redux/slices/statusDefinitionsSlice";
import api from "../../api/axiosInstance";
import KpiCard from "../../components/common/KpiCard";
import StatusChip from "../../components/common/StatusChip";
import ShipmentTracker from "../../components/common/ShipmentTracker";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import NewShipmentSplitButton from "../../components/common/NewShipmentSplitButton";

dayjs.extend(relativeTime);

// `bg` values are MUI theme token strings (resolved wherever they're used
// as an sx `bgcolor`), not literal hex — so each adapts to light/dark mode
// automatically instead of staying a fixed pastel.
const NOTIF_ICONS = {
  shipment_update: { icon: "📦", bg: "info.light" },
  sailing_update: { icon: "⛵", bg: "rgba(147,51,234,0.14)" },
  payment_reminder: { icon: "💳", bg: "error.light" },
  customs_alert: { icon: "📋", bg: "warning.light" },
  document_required: { icon: "⚠️", bg: "warning.light" },
  pickup_ready: { icon: "🎉", bg: "success.light" },
  general: { icon: "🔔", bg: "action.hover" },
};

const CustomerDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const activeTin = useSelector(selectActiveTin);
  const shipments = useSelector(selectShipments);
  const shipmentsLoading = useSelector(selectShipmentsLoading);
  const notifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadCount);
  const unclaimedCount = useSelector(selectMyUnclaimedPackagesCount);
  const scheduledForDeliveryKey = useSelector(selectKeyForRole("shipment", "triggers_delivery_pool"));
  const [notificationsLoading, setNotificationsLoading] = React.useState(true);

  // Outstanding invoice (fetched separately)
  const [invoice, setInvoice] = React.useState(null);
  const [invoiceLoading, setInvoiceLoading] = React.useState(false);

  useEffect(() => {
    dispatch(fetchShipments({ limit: 10, tin: activeTin || undefined }));
    setNotificationsLoading(true);
    dispatch(fetchNotifications({ limit: 5 })).finally(() => setNotificationsLoading(false));

    // Fetch first outstanding invoice
    const loadInvoice = async () => {
      setInvoiceLoading(true);
      try {
        const { data } = await api.get("/invoices", {
          params: { status: "sent", limit: 1, tin: activeTin || undefined },
        });
        const list = data.data || [];
        if (list.length > 0) setInvoice(list[0]);
      } catch {
        /* no invoice */
      } finally {
        setInvoiceLoading(false);
      }
    };
    loadInvoice();
  }, [dispatch, activeTin]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const activeShipments = shipments.filter((s) => s.status !== "delivered");
  const pendingActions = shipments.filter(
    (s) => s.status === "awaiting_invoice",
  );
  const readyForPickup = shipments.filter(
    (s) => s.status === scheduledForDeliveryKey,
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title={`${greeting()}, ${user?.firstName} 👋`}
        subtitle={
          activeShipments.length > 0
            ? `You have ${activeShipments.length} active shipment${activeShipments.length !== 1 ? "s" : ""}${pendingActions.length > 0 ? ` · ${pendingActions.length} pending action${pendingActions.length !== 1 ? "s" : ""}` : ""}`
            : "Welcome to your Waypoint Imports portal"
        }
        action={
          <NewShipmentSplitButton
            label="New Shipment"
            onNewShipment={() => navigate("/dashboard/shipments")}
            canBulkAdd={!!user?.canBulkAddShipments}
            onBulkAdd={() => navigate("/dashboard/shipments/bulk-add")}
            canBulkImport={!!user?.canBulkImportShipments}
            onBulkImport={() => navigate("/dashboard/shipments/bulk-import")}
          />
        }
      />

      {(user?.canManageMultipleTins ? !user?.tins?.length : !user?.tin) && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            You haven't added a TIN yet — add one in your Profile before creating a shipment.
          </Typography>
        </Alert>
      )}

      {(user?.canManageMultipleTins ? !user?.tins?.length : !user?.tin) && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            Don't Forget to authorize us in your COLS account
          </Typography>
          <Typography variant="body2">Jordan O Seales T/A Waypoint Imports</Typography>
        </Alert>
      )}

      {unclaimedCount > 0 && (
        <Alert
          severity="info"
          sx={{ borderRadius: 2 }}
          action={
            <Button size="small" color="info" onClick={() => navigate("/dashboard/unclaimed-packages")}>
              View
            </Button>
          }
        >
          <Typography variant="body2" fontWeight={600}>
            You have {unclaimedCount} unclaimed package{unclaimedCount === 1 ? "" : "s"} waiting — create a shipment
            with the matching tracking number to claim {unclaimedCount === 1 ? "it" : "them"}.
          </Typography>
        </Alert>
      )}

      {/* Shipping address — translucent tint of primary.main instead of a
          solid light-blue hex: a fixed pastel stayed bright light-blue in
          dark mode while the address text above it (no explicit color, so
          it follows the theme) turned light-on-light and nearly vanished. A
          translucent overlay blends into whichever background is under it
          in either mode, so the theme-following text stays readable. */}
      <Card
        sx={{
          border: "2px solid",
          borderColor: "primary.main",
          bgcolor: "rgba(13,98,155,0.08)",
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="overline"
              color="primary.main"
              fontWeight={700}
              display="block"
            >
              Your Shipping Address
            </Typography>
            <Typography fontWeight={700} fontSize={15}>
              Waypoint Imports - {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              3740 W 104th St Unit #1
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Hialeah, FL 33018
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const addressText = `Waypoint Imports - ${user?.firstName} ${user?.lastName}\n3740 W 104th St Unit #1\nHialeah, FL 33018`;
              navigator.clipboard.writeText(addressText);
            }}
          >
            Copy Address
          </Button>
        </CardContent>
      </Card>

      {/* KPIs */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            lg: "repeat(4, 1fr)",
          },
          gap: 2,
        }}
      >
        <KpiCard
          label="Active Shipments"
          value={shipmentsLoading ? "—" : activeShipments.length.toString()}
          delta={
            activeShipments.length > 0
              ? `${activeShipments.length} in progress`
              : "All delivered"
          }
          deltaType="neutral"
          icon={<LocalShipping />}
          iconBg="#EBF4FF"
          iconColor="#2B6CB0"
        />
        <KpiCard
          label="Pending Actions"
          value={shipmentsLoading ? "—" : pendingActions.length.toString()}
          delta={
            pendingActions.length > 0
              ? "Upload invoice needed"
              : "All up to date"
          }
          deltaType={pendingActions.length > 0 ? "down" : "neutral"}
          icon={<Warning />}
          iconBg="#FFFBEB"
          iconColor="#C05621"
        />
        <KpiCard
          label="Ready for Pickup"
          value={shipmentsLoading ? "—" : readyForPickup.length.toString()}
          delta={
            readyForPickup.length > 0
              ? "Collect from warehouse"
              : "Nothing ready yet"
          }
          deltaType={readyForPickup.length > 0 ? "up" : "neutral"}
          icon={<CheckCircle />}
          iconBg="#ECFDF5"
          iconColor="#0E9F6E"
        />
        <KpiCard
          label="Outstanding Balance"
          value={
            invoiceLoading
              ? "—"
              : invoice
                ? `$${(invoice.balanceDue || 0).toFixed(2)}`
                : "$0.00"
          }
          delta={
            invoice
              ? `Due ${dayjs(invoice.dueDate).format("MMM D")}`
              : "No outstanding invoices"
          }
          deltaType={invoice ? "down" : "neutral"}
          icon={<CreditCard />}
          iconBg={invoice ? "#FFF5F5" : "#ECFDF5"}
          iconColor={invoice ? "#C53030" : "#0E9F6E"}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 340px" },
          gap: 2.5,
        }}
      >
        {/* Active shipments */}
        <Card>
          <CardHeader
            title={
              <Typography fontWeight={700} fontSize={14}>
                My Active Shipments
              </Typography>
            }
            subheader={
              <Typography variant="caption" color="text.secondary">
                Track your packages in real time
              </Typography>
            }
            action={
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate("/dashboard/shipments")}
              >
                View All
              </Button>
            }
            sx={{ pb: 0 }}
          />

          {shipmentsLoading ? (
            <SkeletonLoader rows={3} />
          ) : activeShipments.length === 0 ? (
            <EmptyState
              icon="📦"
              title="No active shipments"
              description="Create your first shipment to start tracking your imports."
              actionLabel="Create Shipment"
              onAction={() => navigate("/dashboard/shipments")}
            />
          ) : (
            activeShipments.slice(0, 3).map((s, idx) => (
              <Box
                key={s._id}
                sx={{
                  px: 2.5,
                  py: 2,
                  borderBottom:
                    idx < Math.min(activeShipments.length, 3) - 1
                      ? "1px solid"
                      : "none",
                  borderColor: "divider",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
                onClick={() => navigate(`/dashboard/shipments/${s.trackingNumber}`)}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Box>
                    <Typography fontWeight={700} fontSize={13}>
                      {s.packageDescription || s.vendor?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[s.vendor?.name, s.orderNumber && `Order #${s.orderNumber}`]
                        .filter(Boolean)
                        .join(" · ") || s.trackingNumber}
                    </Typography>
                  </Box>
                  <StatusChip status={s.status} domain="shipment" />
                </Box>

                {s.status === "awaiting_invoice" ? (
                  <Alert
                    severity="warning"
                    sx={{ borderRadius: 2, py: 0.75 }}
                    action={
                      <Button
                        size="small"
                        color="warning"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dashboard/upload/${s.trackingNumber}`);
                        }}
                      >
                        Upload
                      </Button>
                    }
                  >
                    <Typography variant="caption" fontWeight={600}>
                      Action required — upload your purchase invoice to proceed
                    </Typography>
                  </Alert>
                ) : s.status === scheduledForDeliveryKey ? (
                  <Alert severity="success" sx={{ borderRadius: 2, py: 0.75 }}>
                    <Typography variant="caption" fontWeight={600}>
                      🎉 Your package is ready for pickup at George Town
                      Warehouse!
                    </Typography>
                  </Alert>
                ) : (
                  <Box>
                    <ShipmentTracker status={s.status} />
                    {s.estimatedArrivalDate && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        ETA George Town:{" "}
                        <strong>
                          {dayjs(s.estimatedArrivalDate).format("MMMM D, YYYY")}
                        </strong>
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            ))
          )}
        </Card>

        {/* Right column */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Notifications */}
          <Card>
            <CardHeader
              title={
                <Typography fontWeight={700} fontSize={14}>
                  Notifications
                </Typography>
              }
              action={
                unreadCount > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      bgcolor: "primary.main",
                      color: "white",
                      px: 1,
                      py: 0.25,
                      borderRadius: 99,
                      fontWeight: 700,
                    }}
                  >
                    {unreadCount} new
                  </Typography>
                )
              }
              sx={{ pb: 0 }}
            />
            {notificationsLoading ? (
              <SkeletonLoader rows={4} />
            ) : notifications.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ p: 2.5 }}
              >
                No notifications yet.
              </Typography>
            ) : (
              notifications.slice(0, 4).map((n, idx) => {
                const cfg = NOTIF_ICONS[n.type] || NOTIF_ICONS.general;
                return (
                  <Box
                    key={n._id}
                    onClick={() => {
                      if (!n.isRead) dispatch(markAsRead(n._id));
                    }}
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      px: 2,
                      py: 1.5,
                      borderBottom:
                        idx < notifications.slice(0, 4).length - 1
                          ? "1px solid"
                          : "none",
                      borderColor: "divider",
                      bgcolor: n.isRead
                        ? "transparent"
                        : "rgba(13,98,155,0.02)",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        bgcolor: cfg.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 15,
                        flexShrink: 0,
                      }}
                    >
                      {cfg.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          fontWeight: n.isRead ? 500 : 700,
                        }}
                        noWrap
                      >
                        {n.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                      >
                        {n.message}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 0.5,
                        flexShrink: 0,
                      }}
                    >
                      <Typography variant="caption" color="text.disabled">
                        {dayjs(n.createdAt).fromNow()}
                      </Typography>
                      {!n.isRead && (
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            bgcolor: "primary.main",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })
            )}
            {notifications.length > 4 && (
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Button
                  size="small"
                  fullWidth
                  onClick={() => navigate("/dashboard/notifications")}
                >
                  View all {notifications.length} notifications
                </Button>
              </Box>
            )}
          </Card>

          {/* Outstanding invoice widget */}
          {invoice && (
            <Card>
              <CardHeader
                title={
                  <Typography fontWeight={700} fontSize={14}>
                    Outstanding Invoice
                  </Typography>
                }
                sx={{ pb: 0 }}
              />
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography fontWeight={700} fontSize={13}>
                      {invoice.invoiceNumber}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Issued {dayjs(invoice.issuedAt).format("MMM D, YYYY")}
                    </Typography>
                  </Box>
                  <StatusChip status="sent" domain="invoice" />
                </Box>

                {(invoice.lines || []).map((line, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 0.75,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {line.description}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      ${(line.unitPrice * (line.quantity || 1)).toFixed(2)}
                    </Typography>
                  </Box>
                ))}

                <Divider sx={{ my: 1.5 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography fontWeight={700}>Total Due</Typography>
                  <Typography fontWeight={700} fontSize={17} color="error.main">
                    ${(invoice.balanceDue || 0).toFixed(2)}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{ py: 1.25 }}
                  onClick={() => navigate("/dashboard/payments")}
                >
                  Pay Now — ${(invoice.balanceDue || 0).toFixed(2)}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Quick upload shortcut */}
          {pendingActions.length > 0 && (
            <Card
              sx={{
                border: "1px solid",
                borderColor: "warning.light",
                bgcolor: "#FFFBEB",
              }}
            >
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <Box sx={{ fontSize: 28, flexShrink: 0 }}>⚠️</Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    fontWeight={700}
                    fontSize={13}
                    color="warning.dark"
                  >
                    {pendingActions.length} shipment
                    {pendingActions.length !== 1 ? "s" : ""} awaiting invoice
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Upload invoices to proceed with customs processing
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  startIcon={<UploadFile />}
                  onClick={() => navigate("/dashboard/upload")}
                >
                  Upload
                </Button>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerDashboard;
